/**
 * The console: a page over the public API, routed by the URL's hash.
 *
 *   #/connect                  base URL and key
 *   #/signup                   make a workspace: email, name, Turnstile
 *   #/verify?token=…           the mailed link: the workspace and its first key
 *   #/worlds                   list and create
 *   #/w/{world}[/{tab}]        lens (where built) · grid · occupiers · journal · relays · asks
 *   #/w/{world}/relays/{r}     one relay: standing, windows, acts
 *   #/webhooks                 list, create, delete
 *   #/billing                  plan, usage, checkout and the billing portal
 *   #/mock-checkout, #/mock-portal   stand-ins for Stripe's pages under mock billing
 *
 * A hash router needs no server rewrites, so the console is plain static
 * files on any host.
 */

import type { Api, Preset } from './api';
import { Notices, field, h, mount, nameBox, when } from './dom';
import { worldName } from './names';
import { defaultBase, forgetKey, savedApi, saveSession } from './session';
import { DEFAULT_TAB, renderWorld } from './world';
import { WEBHOOK_EVENTS } from './events';
import { renderBilling, renderMockCheckout, renderMockPortal } from './billing';
import { OPEN_SCREENS, readRoute } from './route';
import { renderSignup, renderVerify } from './signup';

const view = document.getElementById('view')!;
const nav = document.getElementById('nav')!;

let api: Api | null = savedApi();
let cleanup: (() => void) | null = null;

const go = (hash: string): void => {
  if (location.hash === hash) void route();
  else location.hash = hash;
};

const renderNav = (current: string): void => {
  const link = (hash: string, label: string) =>
    h('a', { href: hash, class: current.startsWith(hash) ? 'active' : '' }, label);
  mount(nav, api
    ? [link('#/worlds', 'Worlds'), link('#/webhooks', 'Webhooks'), link('#/billing', 'Billing'),
      h('span', { class: 'base', title: 'the API this console talks to' }, api.base),
      h('button', { class: 'ghost small', onclick: () => { forgetKey(); api = null; go('#/connect'); } }, 'Disconnect')]
    : [link('#/connect', 'Connect')]);
};

async function route(): Promise<void> {
  cleanup?.();
  cleanup = null;
  const hash = location.hash || '#/';
  const { parts, params } = readRoute(hash);
  renderNav(hash);
  if (!api && !OPEN_SCREENS.includes(parts[0] ?? '')) return go('#/connect');

  switch (parts[0]) {
    case 'connect': return renderConnect();
    case 'signup':
      cleanup = renderSignup(view);
      return;
    case 'verify': return renderVerify(view, params.get('token'), (fresh) => { api = fresh; go('#/worlds'); });
    case 'webhooks': return renderWebhooks(api!);
    case 'billing': return renderBilling(view, api!);
    case 'mock-checkout': return renderMockCheckout(view, api!, params.get('plan'));
    case 'mock-portal': return renderMockPortal(view, api!);
    case 'w':
      if (parts[1]) {
        cleanup = renderWorld(view, api!, parts[1], parts[2] ?? DEFAULT_TAB, parts[2] === 'relays' ? parts[3] : undefined);
        return;
      }
      return go('#/worlds');
    default: return renderWorlds(api!);
  }
}

// -- Connect ---------------------------------------------------------------------------------

function renderConnect(): void {
  const notices = new Notices();
  const base = h('input', { type: 'url', required: true, value: api?.base ?? defaultBase(), placeholder: 'https://…', autocomplete: 'url' });
  const key = h('input', { type: 'password', required: true, placeholder: 'sbk_…', autocomplete: 'off', spellcheck: 'false' });
  const submit = async (e: Event) => {
    e.preventDefault();
    const candidate = saveSession(base.value, key.value.trim());
    const answer = await candidate.worlds();
    if (!answer.ok) {
      forgetKey();
      notices.show(answer.problem);
      return;
    }
    api = candidate;
    go('#/worlds');
  };
  mount(view,
    h('section', { class: 'narrow' },
      h('h2', {}, 'Connect'),
      h('p', { class: 'lede' }, 'Point the console at a schedule-bit API and give it a key. The key stays in this tab (sessionStorage) and is sent only to that API.'),
      h('form', { class: 'card', onsubmit: submit },
        field('API base URL', base, 'e.g. https://schedule-bit-api-dev.khaled-mailhub.workers.dev'),
        field('API key', key, 'from sign-up, from /v1/keys, or from the operator'),
        h('button', { class: 'primary', type: 'submit' }, 'Connect')),
      notices.el,
      h('p', { class: 'hint' }, 'No key yet? ', h('a', { href: '#/signup' }, 'Sign up'), ' for a workspace of your own.')));
  key.focus();
}

// -- Worlds ----------------------------------------------------------------------------------

async function renderWorlds(api: Api): Promise<void> {
  const notices = new Notices();
  const list = h('div', { class: 'card' }, h('p', { class: 'hint' }, 'Loading…'));
  const name = h('input', { type: 'text', required: true, placeholder: 'room-1', autocapitalize: 'none' });
  const nameHint = nameBox(name, worldName);
  const preset = h('select', {}, h('option', { value: 'temporal-hour' }, 'temporal-hour'));
  const allowDisplace = h('input', { type: 'checkbox' });
  const tieBreak = h('select', {}, ...['incumbent', 'first-come', 'id'].map((t) => h('option', { value: t }, t)));

  const load = async () => {
    const answer = await api.worlds();
    const worlds = notices.take(answer);
    if (!worlds) return;
    mount(list, worlds.worlds.length === 0
      ? h('p', { class: 'hint' }, 'No Worlds yet. A World is one schedulable resource: a room, a machine, a person.')
      : h('table', {},
        h('thead', {}, h('tr', {}, h('th', {}, 'World'), h('th', {}, 'Preset'), h('th', {}, 'Created'))),
        h('tbody', {}, ...worlds.worlds.map((w) => h('tr', {},
          h('td', {}, h('a', { href: `#/w/${encodeURIComponent(w.name)}` }, w.name)),
          h('td', { class: 'mono' }, w.preset),
          h('td', { class: 'mono muted' }, when(w.createdAt)))))));
  };

  const create = async (e: Event) => {
    e.preventDefault();
    const worldNamed = nameHint.read();
    if (!worldNamed) return name.focus();
    const made = notices.take(await api.createWorld({
      name: worldNamed, preset: preset.value, allowDisplace: allowDisplace.checked, tieBreak: tieBreak.value,
    }));
    if (!made) return;
    notices.ok(`Created ${made.globalName}`);
    name.value = '';
    await load();
  };

  mount(view,
    h('section', {},
      h('div', { class: 'page-head' }, h('h2', {}, 'Worlds')),
      notices.el,
      h('div', { class: 'columns' },
        list,
        h('form', { class: 'card', onsubmit: create },
          h('h3', {}, 'Create a World'),
          field('Name', name, 'any name; saved in lower case, with dashes for spaces', nameHint.hint),
          field('Preset', preset, 'hour: days of 24 hours · minute: hours of 60 minutes'),
          field('Allow displace', allowDisplace, 'a higher rank may take a lower one\'s slots'),
          field('Tie-break', tieBreak, 'how equal ranks settle'),
          h('button', { class: 'primary', type: 'submit' }, 'Create')))));
  const presets = await api.presets();
  if (presets.ok) mount(preset, ...presets.value.presets.map((p: Preset) => h('option', { value: p.id }, `${p.id} (${p.leafSlotBits} per ${p.position})`)));
  await load();
}

// -- Webhooks --------------------------------------------------------------------------------

async function renderWebhooks(api: Api): Promise<void> {
  const notices = new Notices();
  const list = h('div', { class: 'card' }, h('p', { class: 'hint' }, 'Loading…'));
  const secret = h('div', {});
  const url = h('input', { type: 'url', required: true, placeholder: 'https://example.com/hooks/schedule-bit' });
  const every = h('input', { type: 'checkbox', checked: true });
  const boxes = WEBHOOK_EVENTS.map((e) => ({ e, box: h('input', { type: 'checkbox' }) }));

  const load = async () => {
    const hooks = notices.take(await api.webhooks());
    if (!hooks) return;
    mount(list, hooks.webhooks.length === 0
      ? h('p', { class: 'hint' }, 'No webhooks. A workspace may have up to 10.')
      : h('table', {},
        h('thead', {}, h('tr', {}, h('th', {}, 'Id'), h('th', {}, 'URL'), h('th', {}, 'Events'), h('th', {}, 'Created'), h('th', {}, ''))),
        h('tbody', {}, ...hooks.webhooks.map((w) => h('tr', {},
          h('td', { class: 'mono' }, w.id),
          h('td', { class: 'mono wrap' }, w.url),
          h('td', {}, w.events.join(', ')),
          h('td', { class: 'mono muted' }, when(w.createdAt)),
          h('td', {}, h('button', {
            class: 'danger small',
            onclick: async () => {
              if (!notices.take(await api.deleteWebhook(w.id))) return;
              notices.ok(`Deleted ${w.id}`);
              secret.replaceChildren();
              await load();
            },
          }, 'Delete')))))));
  };

  const create = async (e: Event) => {
    e.preventDefault();
    const events = every.checked ? ['*'] : boxes.filter((b) => b.box.checked).map((b) => b.e);
    const made = notices.take(await api.createWebhook(url.value.trim(), events));
    if (!made) return;
    const copy = h('button', {
      class: 'small', type: 'button',
      onclick: async () => {
        try {
          await navigator.clipboard.writeText(made.secret);
          copy.textContent = 'Copied';
        } catch {
          copy.textContent = 'Select and copy it by hand';
        }
      },
    }, 'Copy');
    mount(secret, h('div', { class: 'notice secret' },
      h('div', { class: 'notice-head' }, h('strong', {}, `Signing secret for ${made.id}`), copy),
      h('p', { class: 'detail' }, 'Shown once. Store it now: the API keeps no way to show it again.'),
      h('code', { class: 'secret-value' }, made.secret)));
    url.value = '';
    await load();
  };

  mount(view,
    h('section', {},
      h('div', { class: 'page-head' }, h('h2', {}, 'Webhooks'),
        h('p', { class: 'lede' }, 'Where this workspace\'s events are posted, signed with HMAC-SHA256 and retried with backoff.')),
      notices.el,
      secret,
      h('div', { class: 'columns' },
        list,
        h('form', { class: 'card', onsubmit: create },
          h('h3', {}, 'Add a webhook'),
          field('URL', url, 'https only'),
          field('Every event', every),
          h('fieldset', { class: 'events' }, h('legend', {}, 'or only these'),
            ...boxes.map(({ e, box }) => h('label', { class: 'check' }, box, h('span', { class: 'mono' }, e)))),
          h('button', { class: 'primary', type: 'submit' }, 'Add')))));
  every.addEventListener('change', () => boxes.forEach((b) => { b.box.disabled = every.checked; }));
  boxes.forEach((b) => { b.box.disabled = true; });
  await load();
}

window.addEventListener('hashchange', () => void route());
void route();
